import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SvgComponent } from "@commons/Componentes/SvgComponent/SvgComponent";
import { getComponentWithLoading } from "@commons/Utils";
import useMessage from "@commons/Hooks/useMessage";
import { RevisionDatos } from "./RevisionDatos";
import { RevisionPregunta } from "./RevisionPregunta";
import { RevisionFuentes } from "./RevisionFuentes";
import { RevisionHistorial } from "./RevisionHistorial";
import { RevisionVeredicto } from "./RevisionVeredicto";
import { HtmlSaneado, RevisionSeccion } from "./RevisionSeccion";
import {
    guardarCorreccionesDocumentacion,
    iniciarRevision,
    marcarCorreccionesAplicadas,
    registrarVeredicto
} from "../../Services/conocimientoIaService";
import { RUTA_CITRUSGPT, URL_CONOCIMIENTO_IA_LIST } from "../../Utils/constant";
import "../CitrusGPT/CitrusGPTChat.css";
import "./ConocimientoIA.css";

// El veredicto queda oculto: el asistente responde desde la documentación y no usa lo que se
// apruebe o corrija aquí. Con true vuelve a mostrarse.
const MOSTRAR_VEREDICTO = false;

const armarFormulario = (datos) => ({
    Modulo: datos?.Modulo || "",
    Pantalla: datos?.Pantalla || "",
    TipoError: datos?.TipoError || "",
    Motivo: datos?.Motivo || "",
    RespuestaCorregida: datos?.RespuestaCorregida || "",
    ProponeDocumentacion: Boolean(datos?.ProponeDocumentacion)
});

const ConocimientoIAEditSL = ({ showLoading, hideLoading }) => {
    const { Message, Warning, Error: MostrarError } = useMessage();
    const [parametros] = useSearchParams();
    const idSec = Number.parseInt(parametros.get("IdSec"), 10);
    const idValido = Number.isInteger(idSec) && idSec > 0;

    const [revision, setRevision] = useState(null);
    const [formulario, setFormulario] = useState(armarFormulario(null));
    const [enviando, setEnviando] = useState(null);
    const [bloqueo, setBloqueo] = useState(null);

    const aplicar = useCallback((datos) => {
        setRevision(datos);
        setFormulario(armarFormulario(datos?.Formulario));
    }, []);

    const manejarFallo = useCallback((error, { bloquea = false } = {}) => {
        const mensaje = error?.Mensaje
            || "Ocurrió un error al consultar la respuesta del asistente. Intente de nuevo más tarde.";

        if (bloquea) {
            setBloqueo(mensaje);
            return;
        }
        if (error?.Interno || error?.Tipo === "Error") {
            MostrarError({ message: mensaje });
            return;
        }
        Warning({ message: mensaje });
    }, [MostrarError, Warning]);

    useEffect(() => {
        if (!idValido) {
            window.location.replace(URL_CONOCIMIENTO_IA_LIST);
            return undefined;
        }

        let vigente = true;
        showLoading();
        iniciarRevision(idSec)
            .then((datos) => { if (vigente) aplicar(datos); })
            .catch((error) => { if (vigente) manejarFallo(error, { bloquea: true }); })
            .finally(() => { if (vigente) hideLoading(); });

        return () => { vigente = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idSec, idValido]);

    const emitirVeredicto = async (veredicto) => {
        if (enviando || !revision?.PuedeRevisar) return;

        setEnviando(veredicto);
        try {
            const datos = await registrarVeredicto(idSec, veredicto, formulario);
            aplicar(datos?.Revision);
            if (datos?.Mensaje) Message({ message: datos.Mensaje });
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (error) {
            manejarFallo(error);
        } finally {
            setEnviando(null);
        }
    };

    const guardarCorrecciones = async (correcciones) => {
        try {
            const datos = await guardarCorreccionesDocumentacion(idSec, correcciones);
            if (datos?.Revision) setRevision(datos.Revision);
            if (datos?.Mensaje) Message({ message: datos.Mensaje });
        } catch (error) {
            manejarFallo(error);
        }
    };

    const marcarAplicadas = async (fuentes) => {
        try {
            const datos = await marcarCorreccionesAplicadas(idSec, fuentes);
            if (datos?.Revision) setRevision(datos.Revision);
            if (datos?.Mensaje) Message({ message: datos.Mensaje });
        } catch (error) {
            manejarFallo(error);
        }
    };

    const alAmpliar = useCallback((nueva) => {
        if (nueva) setRevision((actual) => ({ ...actual, Historial: nueva.Historial }));
    }, []);

    const avisar = useCallback((mensaje) => Message({ message: mensaje }), [Message]);

    const respuesta = revision?.Respuesta;

    return (
        <div className="bootstrapiso">
            <div className="citrus-gpt citrus-ia">
                <div className="citrus-gpt-title">
                    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span className="citrus-gpt-icon">
                            <SvgComponent iconName="notes" width="22px" height="22px" fill="currentColor" />
                        </span>
                        <div>
                            <h1>
                                Revisar Respuesta del Asistente
                                {respuesta && <span className="citrus-ia-numero"> #{respuesta.IdSec}</span>}
                            </h1>
                            <p>Decida si lo que contestó el asistente se aprueba, se corrige o se rechaza.</p>
                        </div>
                    </div>

                    <div className="citrus-gpt-title__actions">
                        <a
                            className="citrus-gpt-btn citrus-gpt-btn--link"
                            href={URL_CONOCIMIENTO_IA_LIST}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Volver al listado de respuestas del asistente"
                        >
                            <SvgComponent iconName="search" width="15px" height="15px" fill="currentColor" />
                            Buscar respuestas
                        </a>
                        {revision?.PuedeConsultar && (
                            <a
                                className="citrus-gpt-btn citrus-gpt-btn--link"
                                href={RUTA_CITRUSGPT}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Abrir el chat del asistente de documentación"
                            >
                                <SvgComponent iconName="chat" width="15px" height="15px" fill="currentColor" />
                                Ir al asistente
                            </a>
                        )}
                    </div>
                </div>

                {bloqueo && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--error" role="alert">
                        <SvgComponent iconName="alert" width="18px" height="18px" fill="currentColor" />
                        <span>{bloqueo}</span>
                    </div>
                )}

                {!bloqueo && respuesta && (
                    <div className="citrus-ia-contenido">
                        <RevisionDatos respuesta={respuesta} />

                        <div className={`citrus-ia-revision${MOSTRAR_VEREDICTO ? "" : " citrus-ia-revision--una-columna"}`}>
                            <div className="citrus-ia-revision__lectura">
                                <RevisionPregunta
                                    preguntaHtml={respuesta.PreguntaOriginalHtml}
                                    contexto={revision.Contexto}
                                />

                                <RevisionSeccion titulo="Respuesta generada por la IA" icono="chat">
                                    <HtmlSaneado html={respuesta.RespuestaHtml} className="citrus-ia-html--respuesta" />
                                </RevisionSeccion>

                                <RevisionFuentes
                                    idSec={idSec}
                                    documentos={revision.Documentos}
                                    moduloRespuesta={respuesta.ContextoModulo}
                                    puedeRevisar={Boolean(revision.PuedeRevisar)}
                                    maxLargo={revision.MaxLargoTexto || 4000}
                                    onGuardarCorrecciones={guardarCorrecciones}
                                    onMarcarAplicadas={marcarAplicadas}
                                    onAmpliado={alAmpliar}
                                    onMensaje={avisar}
                                    onFallo={manejarFallo}
                                />
                                <RevisionHistorial historial={revision.Historial} />
                            </div>

                            {MOSTRAR_VEREDICTO && (
                                <div className="citrus-ia-revision__veredicto">
                                    <RevisionVeredicto
                                        formulario={formulario}
                                        onChange={setFormulario}
                                        tiposError={revision.TiposError || []}
                                        puedeRevisar={Boolean(revision.PuedeRevisar)}
                                        enviando={enviando}
                                        onVeredicto={emitirVeredicto}
                                        urlVolver={URL_CONOCIMIENTO_IA_LIST}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const ConocimientoIAEdit = getComponentWithLoading(ConocimientoIAEditSL);
